import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { load } from "@cashfreepayments/cashfree-js";
import { use } from "react";

const API_URL = process.env.REACT_APP_API_URL;
const token = localStorage.getItem("authToken");

const Checkout = ({ selectedCurrency }) => {
  const [errors, setErrors] = useState({});
  const [selectedPayment, setSelectedPayment] = useState("payment1");
  const [cart, setCart] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("cod");

  const TAX = 0;
  const DELIVERY = 0;

  const Toast = Swal.mixin({
    toast: true,
    position: "bottom-end",
    showConfirmButton: false,
    timer: 2000,
    timerProgressBar: true,
  });

  const errorStyle = {
    border: "1px solid #e53935",
    backgroundColor: "#ffeaea",
  };

  const [billing, setBilling] = useState({
    firstName: "",
    lastName: "",
    address1: "",
    address2: "",
    city: "",
    pincode: "",
    state: "",
    country: "",
    email: "",
    phone: "",
  });

  const [sameAsBilling, setSameAsBilling] = useState(true);

  const [shipping, setShipping] = useState({
    firstName: "",
    lastName: "",
    address1: "",
    address2: "",
    city: "",
    pincode: "",
    state: "",
    country: "",
    email: "",
    phone: "",
  });

  useEffect(() => {
    fetchUserAddress();
  }, []);

  // const userId = localStorage.getItem("userId")
  const fetchUserAddress = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const user = res.data.user;

      // console.log(user.address.fullAddress);

      if (user.address) {
        setBilling({
          firstName: user.first_name || "",
          lastName: user.last_name || "",
          email: user.email || "",
          phone: user.phone || "",
          address1: user.address.fullAddress || "",
          address2: user.address.fullAddress || "",
          city: user.address.city || "",
          state: user.address.state || "",
          pincode: user.address.pincode || "",
          country: user.address.country || "",
        });
      }
    } catch (err) {
      console.log("Error fetching address", err);
    }
  };

  const handleBillingChange = (e) => {
    const { name, value } = e.target;

    // only digits allowed in phone/pincode
    if (name === "phone") {
      if (!/^\d*$/.test(value)) return;
      if (value.length > 10) return;
    }
    if (name === "pincode") {
      // Allow letters, numbers, spaces and hyphens
      if (!/^[A-Za-z0-9\s-]*$/.test(value)) return;

      // Max length 12 (adjust as needed)
      if (value.length > 12) return;
    }
    const updatedBilling = { ...billing, [name]: value };
    setBilling(updatedBilling);

    if (sameAsBilling) {
      setShipping(updatedBilling);
    }
  };

  const handleShippingChange = (e) => {
    setShipping({ ...shipping, [e.target.name]: e.target.value });
  };

  const toggleSameAsBilling = () => {
    const newValue = !sameAsBilling;
    setSameAsBilling(newValue);

    if (newValue) {
      setShipping(billing);
    }
  };

  const getData = async () => {
    const locart = localStorage.getItem("cart");
    if (locart) setCart(JSON.parse(locart));

    if (token) {
      let cartdata = await axios.get(`${API_URL}/cart`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (locart.length < cartdata.data.cart.length) {
        setCart(cartdata.data.cart);
      }
    }
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (!cart.length) return;

    const updatedCart = cart.map((item) => ({
      ...item,

      currency: selectedCurrency,

      price:
        item.allPrices?.[selectedCurrency] || item.allPrices?.INR || item.price,
    }));

    setCart(updatedCart);

    localStorage.setItem("cart", JSON.stringify(updatedCart));
  }, [selectedCurrency]);

  const calculateSubtotal = (item) => {
    const currentPrice =
      item.allPrices?.[selectedCurrency] ||
      item.allPrices?.INR ||
      item.price ||
      0;

    return currentPrice * item.qty;
  };

  const calculateTotal = () =>
    cart.reduce((sum, item) => sum + calculateSubtotal(item), 0);

  const handlePaymentChange = (e) => {
    setPaymentMethod(e.target.value);
  };

  const validateCheckout = () => {
    let temp = {};

    if (!billing.firstName.trim()) temp.firstName = "Required";

    if (!/^[0-9]{10}$/.test(billing.phone)) {
      temp.phone = "Phone must be 10 digits";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(billing.email)) {
      temp.email = "Invalid email format";
    }
    if (!billing.address1.trim()) temp.address1 = "Required";
    if (!billing.city.trim()) temp.city = "Required";
    if (!billing.state.trim()) temp.state = "Required";
    if (!billing.pincode.trim()) temp.pincode = "Required";

    if (!sameAsBilling) {
      if (!shipping.firstName.trim()) temp.shipping_firstName = "Required";
      if (!shipping.lastName.trim()) temp.shipping_lastName = "Required";
      if (!shipping.address1.trim()) temp.shipping_address1 = "Required";
      if (!shipping.city.trim()) temp.shipping_city = "Required";
      if (!shipping.state.trim()) temp.shipping_state = "Required";
      if (!shipping.pincode.trim()) temp.shipping_pincode = "Required";
      if (!/^[0-9]{10}$/.test(shipping.phone))
        temp.shipping_phone = "Invalid Phone";
      if (!emailRegex.test(shipping.email))
        temp.shipping_email = "Invalid Email";
    }

    setErrors(temp);

    return Object.keys(temp).length === 0;
  };

  const handlePlaceOrder = async () => {
    if (!validateCheckout()) {
      Toast.fire({
        icon: "error",
        title: "Please fix the highlighted fields.",
      });
      return;
    }

    try {
      const orderData = {
        userId: localStorage.getItem("userId"),
        currency: selectedCurrency,
        billing,
        shipping: sameAsBilling ? billing : shipping,
        paymentMethod,
        // items: cart.map((item) => ({
        //   ...item,
        //   price: convertCurrency(item.price, selectedCurrency),
        // })),
        items: cart.map((item) => ({
          ...item,
          currency: selectedCurrency,
          price:
            item.allPrices?.[selectedCurrency] ||
            item.allPrices?.INR ||
            item.price,
          oldPrice:
            item.allOldPrices?.[selectedCurrency] ||
            item.allOldPrices?.INR ||
            null,
        })),
        subtotal: calculateTotal(),
        tax: TAX,
        delivery: DELIVERY,
        totalAmount: calculateTotal() + TAX + DELIVERY,
      };

      // COD order
      if (paymentMethod === "cod") {
        const res = await axios.post(`${API_URL}/order/create`, orderData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (res.data.success) {
          Swal.fire({
            icon: "success",
            title: "Order Placed!",
            text: "Thank you for shopping with us",
          }).then(() => {
            localStorage.removeItem("cart");
            window.location.href = "/";
          });
        }
      } else if (paymentMethod === "online") {
        try {
          Toast.fire({
            icon: "info",
            title: "Redirecting to secure payment...",
          });

          const currency = localStorage
            .getItem("selectedCurrency")
            .toUpperCase();
          const url =
            currency === "INR"
              ? `${API_URL}/payment/create-order`
              : `${API_URL}/payment/create-stripe-order`;

          localStorage.setItem("pendingOrder", JSON.stringify(orderData));

          const res = await axios.post(
            url,
            { ...orderData, currency, paymentMethod },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            },
          );

          localStorage.setItem("stripeOrderId", res.data.orderId);

          if (!res.data.success) {
            throw new Error("Payment session creation failed");
          }

          if (currency === "INR") {
            const { payment_session_id } = res.data;

            const cashfree = await load({
              // mode: "sandbox",
              mode: "production",
            });

            cashfree.checkout({
              paymentSessionId: payment_session_id,
              redirectTarget: "_self",
            });
          } else {
            const { stripeSessionId } = res.data;

            const stripe = window.Stripe(
              "pk_test_51THJbcLLTUAVaOcJcXLzEnkhrHNJ31MxH3hkuqDUDzaXB7vcD3mmsZ8fTIYJkXEJZODc07cLLtrSCnRR3t9exbCh00dw9qV6D0",
            );

            await stripe.redirectToCheckout({
              sessionId: stripeSessionId,
            });
          }
        } catch (error) {
          console.error(error);
          Swal.fire({
            icon: "error",
            title: "Payment Failed",
            text: "Unable to start payment process.",
          });
        }
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Order Failed!",
        text: "Something went wrong while placing your order.",
      });
    }
  };

  return (
    <section className="checkout py-80">
      <div className="container container-lg">
        <div className="border border-gray-100 rounded-8 px-30 py-20 mb-40">
          <span>
            Have a coupon?{" "}
            <Link to="/cart-one" className="fw-semibold text-gray-900">
              Click here to enter your code
            </Link>
          </span>
        </div>

        <div className="row">
          <div className="col-xl-9 col-lg-8">
            <form className="pe-xl-5">
              <h4 className="fw-semibold text-gray-900">Billing Address</h4>

              <div className="row gy-3">
                <div className="col-sm-6">
                  <input
                    name="firstName"
                    value={billing.firstName}
                    onChange={handleBillingChange}
                    type="text"
                    className="common-input"
                    style={errors.firstName ? errorStyle : {}}
                    placeholder="First Name *"
                  />
                </div>

                <div className="col-sm-6">
                  <input
                    name="lastName"
                    value={billing.lastName}
                    onChange={handleBillingChange}
                    type="text"
                    className="common-input"
                    style={errors.lastName ? errorStyle : {}}
                    placeholder="Last Name"
                  />
                </div>

                <div className="col-12">
                  <input
                    name="address1"
                    value={billing.address1}
                    onChange={handleBillingChange}
                    type="text"
                    className="common-input"
                    style={errors.address1 ? errorStyle : {}}
                    placeholder="House/Flat No., Building Name *"
                  />
                </div>

                <div className="col-12">
                  <input
                    name="address2"
                    value={billing.address2}
                    onChange={handleBillingChange}
                    type="text"
                    className="common-input"
                    style={errors.address2 ? errorStyle : {}}
                    placeholder="Street, Area, Landmark (Optional)"
                  />
                </div>

                <div className="col-12">
                  <input
                    name="city"
                    value={billing.city}
                    onChange={handleBillingChange}
                    type="text"
                    className="common-input"
                    style={errors.city ? errorStyle : {}}
                    placeholder="City *"
                  />
                </div>

                <div className="col-12">
                  <input
                    name="pincode"
                    value={billing.pincode}
                    onChange={handleBillingChange}
                    type="text"
                    className="common-input"
                    style={errors.pincode ? errorStyle : {}}
                    placeholder="Pincode *"
                  />
                </div>

                <div className="col-12">
                  <input
                    name="state"
                    value={billing.state}
                    onChange={handleBillingChange}
                    type="text"
                    className="common-input"
                    style={errors.state ? errorStyle : {}}
                    placeholder="State *"
                  />
                </div>

                {/* <div className="col-12">
                  <input
                    disabled
                    value="India"
                    type="text"
                    className="common-input"
                  />
                </div> */}
                <div className="col-12">
                  <input
                    name="country"
                    value={billing.country}
                    onChange={handleBillingChange}
                    type="text"
                    className="common-input"
                    style={errors.country ? errorStyle : {}}
                    placeholder="Country *"
                  />
                </div>

                <div className="col-12">
                  <input
                    name="email"
                    value={billing.email}
                    onChange={handleBillingChange}
                    type="email"
                    className="common-input"
                    style={errors.email ? errorStyle : {}}
                    placeholder="Email *"
                  />
                </div>

                <div className="col-12">
                  <input
                    name="phone"
                    value={billing.phone}
                    onChange={handleBillingChange}
                    type="text"
                    className="common-input"
                    style={errors.phone ? errorStyle : {}}
                    placeholder="Phone *"
                  />
                </div>
              </div>

              <div className="mt-40">
                <h4 className="mb-3">Shipping Information</h4>

                <div className="form-check mb-3">
                  <input
                    type="checkbox"
                    checked={sameAsBilling}
                    onChange={toggleSameAsBilling}
                    className="form-check-input"
                  />
                  <label className="form-check-label fw-bold">
                    Same as Billing Address
                  </label>
                </div>

                {!sameAsBilling && (
                  <div className="row gy-3">
                    <div className="col-sm-6">
                      <input
                        name="firstName"
                        value={shipping.firstName}
                        onChange={handleShippingChange}
                        type="text"
                        className="common-input"
                        style={errors.shipping_firstName ? errorStyle : {}}
                        placeholder="First Name *"
                      />
                    </div>

                    <div className="col-sm-6">
                      <input
                        name="lastName"
                        value={shipping.lastName}
                        onChange={handleShippingChange}
                        type="text"
                        className="common-input"
                        style={errors.shipping_lastName ? errorStyle : {}}
                        placeholder="Last Name *"
                      />
                    </div>

                    <div className="col-12">
                      <input
                        name="address1"
                        value={shipping.address1}
                        onChange={handleShippingChange}
                        type="text"
                        className="common-input"
                        style={errors.shipping_address1 ? errorStyle : {}}
                        placeholder="House/Flat No., Building Name *"
                      />
                    </div>

                    <div className="col-12">
                      <input
                        name="address2"
                        value={shipping.address2}
                        onChange={handleShippingChange}
                        type="text"
                        className="common-input"
                        style={errors.address2 ? errorStyle : {}}
                        placeholder="Street, Area, Landmark"
                      />
                    </div>

                    <div className="col-12">
                      <input
                        name="city"
                        value={shipping.city}
                        onChange={handleShippingChange}
                        type="text"
                        className="common-input"
                        style={errors.shipping_city ? errorStyle : {}}
                        placeholder="City *"
                      />
                    </div>

                    <div className="col-12">
                      <input
                        name="pincode"
                        value={shipping.pincode}
                        onChange={handleShippingChange}
                        type="text"
                        className="common-input"
                        style={errors.shipping_pincode ? errorStyle : {}}
                        placeholder="Pincode *"
                      />
                    </div>

                    <div className="col-12">
                      <input
                        name="state"
                        value={shipping.state}
                        onChange={handleShippingChange}
                        type="text"
                        className="common-input"
                        style={errors.shipping_state ? errorStyle : {}}
                        placeholder="State *"
                      />
                    </div>

                    <div className="col-12">
                      <input
                        disabled
                        value="India"
                        type="text"
                        className="common-input"
                      />
                    </div>

                    <div className="col-12">
                      <input
                        name="email"
                        value={shipping.email}
                        onChange={handleShippingChange}
                        type="email"
                        className="common-input"
                        style={errors.shipping_email ? errorStyle : {}}
                        placeholder="Email *"
                      />
                    </div>

                    <div className="col-12">
                      <input
                        name="phone"
                        value={shipping.phone}
                        onChange={handleShippingChange}
                        type="text"
                        className="common-input"
                        style={errors.shipping_phone ? errorStyle : {}}
                        placeholder="Phone *"
                      />
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>

          <div className="col-xl-3 col-lg-4">
            <div className="mt-32">
              <h5 className="fw-semibold mb-3">Payment Methods</h5>

              {/* COD */}
              <div className="payment-item">
                <label className="form-check common-check common-radio py-16 mb-0">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="payment"
                    value="cod"
                    checked={paymentMethod === "cod"}
                    onChange={handlePaymentChange}
                  />
                  <span className="form-check-label fw-semibold text-neutral-600">
                    Cash On Delivery
                  </span>
                </label>

                {paymentMethod === "cod" && (
                  <div className="payment-item__content px-16 py-24 rounded-8 bg-main-50">
                    <p className="text-gray-800">
                      Pay cash when your order gets delivered.
                    </p>
                  </div>
                )}
              </div>

              <div className="payment-item">
                <label className="form-check common-check common-radio py-16 mb-0">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="payment"
                    value="online"
                    checked={paymentMethod === "online"}
                    onChange={handlePaymentChange}
                  />
                  <span className="form-check-label fw-semibold text-neutral-600">
                    Online Payment (UPI / Card / Netbanking)
                  </span>
                </label>

                {paymentMethod === "online" && (
                  <div className="payment-item__content px-16 py-24 rounded-8 bg-main-50">
                    <p className="text-gray-800">
                      You will be redirected to secure Razorpay Payment Gateway.
                    </p>
                  </div>
                )}
              </div>

              {/* <div className="payment-item">
                <label className="form-check common-check common-radio py-16 mb-0">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="payment"
                    value="bank"
                    checked={paymentMethod === "bank"}
                    onChange={handlePaymentChange}
                  />
                  <span className="form-check-label fw-semibold text-neutral-600">
                    Direct Bank Transfer
                  </span>
                </label>

                {paymentMethod === "bank" && (
                  <div className="payment-item__content px-16 py-24 rounded-8 bg-main-50">
                    <p className="text-gray-800">
                      Transfer to our bank account. Use your Order ID as payment
                      reference.
                    </p>
                  </div>
                )}
              </div> */}
            </div>

            <div className="checkout-sidebar">
              <div className="border border-gray-100 rounded-8 px-24 py-40 mt-24">
                {cart.map((item) => (
                  <div key={item.id} className="flex-between mb-32">
                    <span>
                      {item.name} × {item.qty}
                    </span>
                    <span>
                      {selectedCurrency} {calculateSubtotal(item).toFixed(2)}
                    </span>
                  </div>
                ))}

                <div className="flex-between">
                  <strong>Subtotal</strong>
                  <span>
                    {selectedCurrency} {calculateTotal().toFixed(2)}
                  </span>
                </div>

                <div className="flex-between mt-2">
                  <strong>Tax</strong>
                  <span>
                    {selectedCurrency} {TAX.toFixed(2)}
                  </span>
                </div>

                <div className="flex-between mt-2">
                  <strong>Total</strong>
                  <span>
                    {selectedCurrency}{" "}
                    {(calculateTotal() + TAX + DELIVERY).toFixed(2)}
                  </span>
                </div>
              </div>

              <button
                onClick={handlePlaceOrder}
                className="btn btn-main mt-40 py-18 w-100"
              >
                Place Order
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Checkout;
